// frontend/src/components/portfolio/ThemeSettingsPanel.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Card,
  CardMedia,
  CardContent
} from '@mui/material';
import {
  Close as CloseIcon,
  Palette as PaletteIcon,
  Delete as DeleteIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import MediaPicker from './MediaPicker';

interface ThemeSettingsPanelProps {
  open: boolean;
  onClose: () => void;
  theme: {
    primaryColor: string;
    accentColor: string;
    fontFamily?: string;
    logo?: string;
    watermark?: {
      url: string;
      position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
      opacity: number;
    };
  };
  onUpdate: (theme: {
    primaryColor: string;
    accentColor: string;
    fontFamily?: string;
    logo?: string;
    watermark?: {
      url: string;
      position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
      opacity: number;
    };
  }) => void;
}

const FONT_OPTIONS = [
  { value: 'Inter', label: 'Inter' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Playfair Display', label: 'Playfair Display' },
  { value: 'Merriweather', label: 'Merriweather' }
];

const ThemeSettingsPanel: React.FC<ThemeSettingsPanelProps> = ({
  open,
  onClose,
  theme,
  onUpdate
}) => {
  const [primaryColor, setPrimaryColor] = useState(theme.primaryColor || '#000000');
  const [accentColor, setAccentColor] = useState(theme.accentColor || '#FF0000');
  const [fontFamily, setFontFamily] = useState(theme.fontFamily || 'Inter');
  const [logo, setLogo] = useState(theme.logo || '');
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);

  // Update local state when theme prop changes
  useEffect(() => {
    setPrimaryColor(theme.primaryColor || '#000000');
    setAccentColor(theme.accentColor || '#FF0000');
    setFontFamily(theme.fontFamily || 'Inter');
    setLogo(theme.logo || '');
  }, [theme]);

  const handleSave = () => {
    onUpdate({
      primaryColor,
      accentColor,
      fontFamily,
      logo
    });
    onClose();
  };

  const handleCancel = () => {
    // Reset to original values
    setPrimaryColor(theme.primaryColor || '#000000');
    setAccentColor(theme.accentColor || '#FF0000');
    setFontFamily(theme.fontFamily || 'Inter');
    setLogo(theme.logo || '');
    onClose();
  };

const handleLogoSelect = (imageUrl: string) => {
  // If the logo slot was empty, assume this was for logo;
  // otherwise treat it as watermark
  if (!logo) {
    setLogo(imageUrl);
  } else {
    onUpdate({
      ...theme,
      watermark: {
        url: imageUrl,
        position: 'bottom-right',
        opacity: 0.15
      }
    });
  }
  setMediaPickerOpen(false);
};

  const handleRemoveLogo = () => {
    setLogo('');
  };

  const validateHexColor = (color: string): boolean => {
    return /^#[0-9A-F]{6}$/i.test(color);
  };

  const handlePrimaryColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPrimaryColor(value);
  };

  const handleAccentColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAccentColor(value);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2
          }
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 2,
            borderBottom: '1px solid #e0e0e0'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaletteIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Theme Settings
            </Typography>
          </Box>
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Content */}
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Primary Color */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
              >
                Primary Color
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Main brand color used for headings and key elements
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 1,
                    bgcolor: validateHexColor(primaryColor) ? primaryColor : '#000000',
                    border: '2px solid #e0e0e0',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <input
                    type="color"
                    value={validateHexColor(primaryColor) ? primaryColor : '#000000'}
                    onChange={handlePrimaryColorChange}
                    style={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      cursor: 'pointer',
                      opacity: 0
                    }}
                  />
                </Box>
                <TextField
                  value={primaryColor}
                  onChange={handlePrimaryColorChange}
                  placeholder="#000000"
                  size="small"
                  fullWidth
                  error={!validateHexColor(primaryColor)}
                  helperText={!validateHexColor(primaryColor) ? 'Invalid hex color' : ''}
                  InputProps={{
                    sx: { fontFamily: 'monospace' }
                  }}
                />
              </Box>
            </Box>

            {/* Accent Color */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
              >
                Accent Color
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Highlight color used for buttons and calls-to-action
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 1,
                    bgcolor: validateHexColor(accentColor) ? accentColor : '#FF0000',
                    border: '2px solid #e0e0e0',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <input
                    type="color"
                    value={validateHexColor(accentColor) ? accentColor : '#FF0000'}
                    onChange={handleAccentColorChange}
                    style={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      cursor: 'pointer',
                      opacity: 0
                    }}
                  />
                </Box>
                <TextField
                  value={accentColor}
                  onChange={handleAccentColorChange}
                  placeholder="#FF0000"
                  size="small"
                  fullWidth
                  error={!validateHexColor(accentColor)}
                  helperText={!validateHexColor(accentColor) ? 'Invalid hex color' : ''}
                  InputProps={{
                    sx: { fontFamily: 'monospace' }
                  }}
                />
              </Box>
            </Box>

            {/* Font Family */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
              >
                Font Family
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Typography style for the portfolio
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={fontFamily}
                  onChange={(e) => setFontFamily(e.target.value)}
                >
                  {FONT_OPTIONS.map((font) => (
                    <MenuItem
                      key={font.value}
                      value={font.value}
                      sx={{ fontFamily: font.value }}
                    >
                      {font.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: '#92a9d5ff',
                  borderRadius: 1,
                  fontFamily: fontFamily
                }}
              >
                <Typography variant="h6" sx={{ fontFamily: fontFamily, mb: 1 }}>
                  Preview Text
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: fontFamily }}>
                  The quick brown fox jumps over the lazy dog
                </Typography>
              </Box>
            </Box>

            {/* Logo */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
              >
                Logo
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Brand logo displayed on the portfolio
              </Typography>

              {logo ? (
                <Card sx={{ maxWidth: 300 }}>
                  <CardMedia
                    component="img"
                    image={logo}
                    alt="Logo"
                    sx={{
                      height: 120,
                      objectFit: 'contain',
                      bgcolor: '#f5f5f5',
                      p: 2
                    }}
                  />
                  <CardContent sx={{ display: 'flex', gap: 1, p: 1.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setMediaPickerOpen(true)}
                      fullWidth
                    >
                      Change Logo
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={handleRemoveLogo}
                      sx={{ border: '1px solid', borderColor: 'error.main' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardContent>
                </Card>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<ImageIcon />}
                  onClick={() => setMediaPickerOpen(true)}
                  sx={{ width: 'fit-content' }}
                >
                  Upload Logo
                </Button>
              )}
            </Box>
            {/* Watermark */}
<Box>
  <Typography
    variant="subtitle2"
    sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
  >
    Watermark
  </Typography>
  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
    Optional branded overlay displayed on your portfolio pages
  </Typography>

  {theme?.watermark?.url ? (
    <Card sx={{ maxWidth: 300 }}>
      <CardMedia
        component="img"
        image={theme.watermark.url}
        alt="Watermark"
        sx={{
          height: 120,
          objectFit: 'contain',
          bgcolor: '#f5f5f5',
          p: 2
        }}
      />
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
       <FormControl fullWidth size="small">
  <InputLabel>Position</InputLabel>
  <Select
    label="Position"
    value={theme.watermark?.position ?? 'bottom-right'}
    onChange={(e) =>
      onUpdate({
        ...theme,
        watermark: {
          url: theme.watermark?.url ?? '',
          opacity: theme.watermark?.opacity ?? 0.15,
          position: e.target.value as
            | 'top-left'
            | 'top-right'
            | 'bottom-left'
            | 'bottom-right',
        },
      })
    }
  >
    <MenuItem value="top-left">Top Left</MenuItem>
    <MenuItem value="top-right">Top Right</MenuItem>
    <MenuItem value="bottom-left">Bottom Left</MenuItem>
    <MenuItem value="bottom-right">Bottom Right</MenuItem>
  </Select>
</FormControl>

        {/* Opacity */}
       <TextField
  label="Opacity"
  type="number"
  size="small"
  inputProps={{ step: 0.05, min: 0, max: 1 }}
  value={theme.watermark?.opacity ?? 0.15}
  onChange={(e) =>
    onUpdate({
      ...theme,
      watermark: {
        url: theme.watermark?.url ?? '',
        position: theme.watermark?.position ?? 'bottom-right',
        opacity: parseFloat(e.target.value)
      }
    })
  }
/>

        {/* Replace / Remove buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setMediaPickerOpen(true)}
            fullWidth
          >
            Change
          </Button>
          <IconButton
            size="small"
            color="error"
            onClick={() =>
              onUpdate({
                ...theme,
                watermark: { url: '', position: 'bottom-right', opacity: 0.15 }
              })
            }
            sx={{ border: '1px solid', borderColor: 'error.main' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  ) : (
    <Button
      variant="outlined"
      startIcon={<ImageIcon />}
      onClick={() => setMediaPickerOpen(true)}
      sx={{ width: 'fit-content' }}
    >
      Upload Watermark
    </Button>
  )}
</Box>

            {/* Preview Section */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
              >
                Color Preview
              </Typography>
              <Box
                sx={{
                  p: 3,
                  bgcolor: validateHexColor(primaryColor) ? primaryColor : '#000000',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: 'white',
                    fontFamily: fontFamily
                  }}
                >
                  Sample Heading
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontFamily: fontFamily
                  }}
                >
                  This is how your content will look with the selected theme.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    bgcolor: validateHexColor(accentColor) ? accentColor : '#FF0000',
                    color: 'white',
                    width: 'fit-content',
                    fontFamily: fontFamily,
                    '&:hover': {
                      bgcolor: validateHexColor(accentColor) ? accentColor : '#FF0000',
                      opacity: 0.9
                    }
                  }}
                >
                  Call to Action
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        {/* Actions */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #e0e0e0'
          }}
        >
          <Button onClick={handleCancel} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!validateHexColor(primaryColor) || !validateHexColor(accentColor)}
          >
            Save Theme
          </Button>
        </DialogActions>
      </Dialog>

      {/* Media Picker Modal */}
      <MediaPicker
        open={mediaPickerOpen}
        onClose={() => setMediaPickerOpen(false)}
        onSelect={handleLogoSelect}
      />
    </>
  );
};

export default ThemeSettingsPanel;  